import { TextareaInputField } from "@/components/shared";
import { CircleLoader } from "@/components/shared/loaders";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTenderPermissionsData } from "@/hooks/cache";
import { zodResolver } from "@hookform/resolvers/zod";
import { TFunction } from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const createWonLostDecisionSchema = (t: TFunction) => z.object({
  won: z.boolean(),
  reasonForLoss: z.string().optional()
}).check((ctx) => {
  if (ctx.value.won === false && !ctx.value.reasonForLoss) {
    ctx.issues.push({
      code: "custom",
      message: t("components:tendersubmission.wonlost.inputs.reasonForLoss.required"),
      path: ["reasonForLoss"],
      input: ctx.value.reasonForLoss,
    });
  }
});

interface WonLostOptionComponentProps {
  updateTenderStateMutation: any;
  loading: boolean;
  tenderID: string | undefined;
};

function WonLostOptionComponent({ updateTenderStateMutation, loading, tenderID }: WonLostOptionComponentProps) {
  const { t } = useTranslation(["components"]);
  const { canDeclareWonLostOutcome } = useTenderPermissionsData(Number(tenderID));
  const wonLostDecisionSchema = createWonLostDecisionSchema(t);

  const form = useForm<z.infer<typeof wonLostDecisionSchema>>({
    resolver: zodResolver(wonLostDecisionSchema),
    defaultValues: {
      won: undefined,
      reasonForLoss: undefined
    },
    mode: "onChange",
  });

  const outcomeDecision = form.watch("won");

  const handleOnWonLossDecision = (data: z.infer<typeof wonLostDecisionSchema>) => {
    updateTenderStateMutation({
      variables: {
        tenderId: Number(tenderID),
        won: data.won,
        ...(data.won === false && { reasonForLoss: data.reasonForLoss })
      }
    });
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">{t("tendersubmission.wonlost.title")}</CardTitle>
          <CardDescription>{t("tendersubmission.wonlost.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleOnWonLossDecision)} className="flex-col-4">
              <FormField
                control={form.control}
                name="won"
                render={({ field }) => (
                  <FormItem>
                    <Select onValueChange={(value) => field.onChange(value === "true")}>
                      <FormControl>
                        <SelectTrigger disabled={!canDeclareWonLostOutcome}>
                          <SelectValue placeholder={t("tendersubmission.wonlost.inputs.won.placeholder")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="true">
                          {t("tendersubmission.wonlost.inputs.won.won")}
                        </SelectItem>
                        <SelectItem value="false">
                          {t("tendersubmission.wonlost.inputs.won.lost")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {
                outcomeDecision === false && (
                  <TextareaInputField
                    form={form}
                    name="reasonForLoss"
                    label={t("tendersubmission.wonlost.inputs.reasonForLoss.label")}
                    placeholder={t("tendersubmission.wonlost.inputs.reasonForLoss.placeholder")}
                    className="w-full"
                    isFormItem={false}
                  />
                )
              }

              <div className="flex justify-end">
                <Button disabled={loading || !canDeclareWonLostOutcome} type="submit" className="gap-2">
                  <CircleLoader visible={loading} height={16} width={16} strokeWidth={2} />
                  {t("common:buttons.submit")}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}

export default WonLostOptionComponent;
